"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import Image from "next/image";
import TypewriterEffect from "@/components/TypewriterEffect";
import { Button } from "@/components/ui/button";

interface TheoryIntroProps {
  /**
   * true 면 타자기 애니메이션 없이 전체 내용을 정적으로 즉시 노출 (이미 본 적 있는 재방문).
   * false(기본)면 첫 방문 — 위에서부터 타자기 애니메이션으로 펼친다. JCG-2099.
   * 인트로는 숨겨지지 않고 항상 보여진 상태로 유지된다(숨기기 버튼 없음).
   */
  revealed?: boolean;
}

export default function TheoryIntro({ revealed = false }: TheoryIntroProps) {
  const [visibleIndex, setVisibleIndex] = useState(0);
  const [isImpatient, setIsImpatient] = useState(false);

  const handleComplete = useCallback(() => {
    setVisibleIndex((prev) => prev + 1);
  }, []);

  const handleImpatientClick = () => {
    setIsImpatient(true);
  };

  const elements = useMemo(
    () => [
      <TypewriterEffect
        key={0}
        className="text-center text-base mt-12"
        text="범위가 너무 넓어서 공부하기 막막한 정보처리기사 실기 이론 파트... 버리려고 하셨나요?"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={1}
        className="text-center text-base mt-12"
        text="그럼 감자가 솔직하게 이야기해드릴게요."
        onComplete={handleComplete}
      />,
      <p key={2} className="text-center text-2xl mt-12">
        정보처리기사 실기 이론 파트 버리고 가는 방법도 있습니다
      </p>,
      <Image
        key={3}
        className="mt-12 mb-12 mx-auto"
        src="/theory/dodongtak_gamza.webp"
        alt="shock"
        width={125}
        height={125}
      />,
      <TypewriterEffect
        key={4}
        className="text-center text-base mt-4"
        text="프로그래밍 파트를 전부다 맞히고 이론 파트는 한 개만 찍어서 맞추면 합격이거든요"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={5}
        className="text-center text-base mt-12"
        text="하지만 여러분."
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={6}
        className="text-center text-xl mt-4"
        text="프로그래밍 파트 다 맞추기 정말 정말 어렵습니다."
        onComplete={handleComplete}
      />,
      <p key={7} className="text-center text-base font-semibold mt-8">
        난 자신있다구요?
      </p>,
      <Image
        key={8}
        className="mt-8 mx-auto"
        src="/theory/yesyes_gamza.webp"
        alt="shock"
        width={125}
        height={125}
      />,
      <TypewriterEffect
        key={9}
        className="text-center text-base mt-4"
        text="저를 다음 회차에서 다시 보진 않았으면 해요"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={10}
        className="text-center text-2xl font-semibold mt-4"
        text="그런데 이론 파트에서 7문제 맞히는건 쉬워요"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={11}
        className="text-center text-base mt-4"
        text="분명 7문제는 나올만한 부분에서 나오거든요"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={12}
        className="text-center text-2xl text-destructive font-semibold mt-4"
        text="2025년도 2,3회 실기에서는 제가 찍은 부분에서 무려 9문제가 나왔어요"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={13}
        className="text-center text-base mt-4"
        text="대박이죠?"
        onComplete={handleComplete}
      />,
      <Image
        key={14}
        className="mt-8 mx-auto"
        src="/theory/pride_gamza.webp"
        alt="shock"
        width={125}
        height={125}
      />,
      <TypewriterEffect
        key={15}
        className="text-center text-base mt-4"
        text="이번 2026년도 1회차 실기에서는 힘내서 11문제 다 예상할 수 있도록 해볼게요"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={16}
        className="text-center text-base mt-12"
        text="프로그래밍 파트는 제가 다루진 않을 거에요(체력이 부족하거든요)"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={17}
        className="text-center text-base mt-4"
        text="나중에 다룰지도 모르지만 프로그래밍 파트 잘하시는 그 쌤도 있으니까 그 분 믿고 공부해봐요"
        onComplete={handleComplete}
      />,
      <Image
        key={18}
        className="mt-8 mx-auto"
        src="/theory/gamdalssam_gamza.webp"
        alt="shock"
        width={125}
        height={125}
      />,
      <TypewriterEffect
        key={19}
        className="text-center text-base mt-4"
        text="분명 프로그래밍 파트에서 5문제는 맞출 수 있을거에요"
        onComplete={handleComplete}
      />,
      <p
        key={20}
        className="text-center text-lg font-semibold text-destructive mt-12"
      >
        우리의 목표는 이론 파트 7문제 + 프로그래밍 파트 5문제 맞혀서 총 12문제를
        맞히는 거에요
      </p>,
      <TypewriterEffect
        key={21}
        className="text-center text-base mt-4"
        text="60점으로 합격하는 거죠"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={22}
        className="text-center text-base mt-4"
        text="자격증은 턱걸이로 땄을 때 가장 행복한거 아시죠?"
        onComplete={handleComplete}
      />,
      <TypewriterEffect
        key={23}
        className="text-center text-base mt-4"
        text="그럼 공부해보자구요!"
        onComplete={handleComplete}
      />,
    ],
    [handleComplete]
  );

  // 전체 내용을 타자기 없이 정적으로 나열(재방문·건너뛰기 공용).
  const staticElements = useMemo(
    () =>
      elements.map((element) => {
        if (element.type === TypewriterEffect) {
          return (
            <p key={element.key} className={element.props.className}>
              {element.props.text}
            </p>
          );
        }
        return element;
      }),
    [elements]
  );

  useEffect(() => {
    // 재방문(revealed)·건너뛰기(isImpatient)·완료 시 애니메이션 타이머 없음.
    if (revealed || isImpatient || visibleIndex >= elements.length) {
      return;
    }

    const currentElement = elements[visibleIndex];
    if (
      currentElement &&
      (currentElement.type === "p" || currentElement.type === Image)
    ) {
      const timer = setTimeout(handleComplete, 1000); // 1초 후에 다음 요소 표시
      return () => clearTimeout(timer);
    }
  }, [visibleIndex, elements, handleComplete, isImpatient, revealed]);

  // 재방문(이미 본 상태) 또는 "답답혀" 건너뛰기 — 애니메이션 없이 전체 정적 노출, 숨기기 버튼 없음.
  if (revealed || isImpatient) {
    return <div className="px-4 mx-auto">{staticElements}</div>;
  }

  // 첫 방문 — 위에서부터 타자기 애니메이션. 완료되면 버튼 없이 전체가 그대로 유지됨(숨기지 않음).
  return (
    <div className="px-4 mx-auto">
      {elements.slice(0, visibleIndex + 1)}
      {visibleIndex < elements.length && (
        <div className="text-center mt-30">
          <Button onClick={handleImpatientClick} variant="outline">
            답답혀
          </Button>
        </div>
      )}
    </div>
  );
}
